import type { ModelExecution } from "@/shared/agent-display/model-execution";
import { type Discovery, toDiscovery } from "@/shared/agent-story/discovery";
import { getAuthToken } from "@/shared/http/client";
import { beginLoading } from "@/shared/http/loading-bar";
import type { Say } from "./tool-labels";
import { type TurnAttachment, toAttachment } from "./turn-attachments";
import { bodyOf, type StreamInput } from "./turn-body";
import type { ToolTask } from "./use-agents";

/** O que se PEDE mora em `turn-body.ts`, junto da função que o monta; o que
 *  CHEGA mora aqui. Reexportado para ninguém ter que saber dessa divisão. */
export type { PageContext, StreamInput } from "./turn-body";

/**
 * A streamed turn.
 *
 * `fetch` with a manual reader rather than `EventSource`, for two reasons that
 * both rule EventSource out: it cannot send a POST body, and it cannot carry an
 * Authorization header. Reading the body ourselves also gives an abort that
 * actually stops the request.
 */
export interface StreamHandlers {
	onQueued?: (message: { id: string; text: string; origin?: "agent" }, applied: boolean) => void;
	/**
	 * EM QUE CONVERSA ESTE TURNO FICOU GRAVADO.
	 *
	 * O servidor abre a thread quando ninguém a abriu (`turn-thread.ts`) e diz o
	 * id no primeiro quadro. Sem escutá-lo, a janela continuaria a achar que não
	 * está em conversa nenhuma — e o envio seguinte abriria outra, deixando o
	 * anterior órfão. Opcional: um servidor mais antigo não manda este quadro.
	 */
	onSession?: (id: string) => void;
	/** O texto que o servidor ouviu numa nota de voz enviada neste turno. */
	onPrompt?: (text: string) => void;
	/**
	 * CHEGARAM BYTES — e não necessariamente um quadro.
	 *
	 * O servidor bate de 15 em 15 s enquanto o turno corre (`turn-sse.ts`), com uma
	 * linha de comentário que não é evento nenhum e que este leitor descarta. Ela
	 * conta na mesma, e é para isso que este aviso existe: é o que separa um fio
	 * CALADO — um turno a pensar — de um fio MORTO, que é o que o Safari do iOS
	 * deixa para trás quando fecha as ligações de uma página suspensa.
	 */
	onBeat?: () => void;
	onDelta?: (text: string) => void;
	/** `lane` só no fio de uma missão: o escritor paralelo que pensa; sem ela é o Ben. */
	onThinking?: (text: string, lane?: string) => void;
	/** O mesmo pensamento, traduzido para a língua da casa, um parágrafo de cada vez (missão, 04/10). */
	onThought?: (text: string, lane?: string) => void;
	/** A corrida traduz o pensamento para esta língua (missão, 09/10) — o botão PT/EN aparece. */
	onTranslation?: (to: string) => void;
	onToolCall?: (call: { id: string; name: string; input: unknown }) => void;
	onToolResult?: (result: {
		id: string;
		name: string;
		output: string;
		isError: boolean;
		ms: number;
	}) => void;
	onStep?: (index: number) => void;
	/** A lista INTEIRA de tarefas de um passo em curso — o filho do `spawn_agent`
	 *  trabalhando. Opcional: um servidor mais antigo não o manda. */
	onToolTasks?: (progress: { id: string; tasks: readonly ToolTask[] }) => void;
	/**
	 * O modelo está ESCREVENDO uma peça grande — a tela, o artifact. `chars` é o
	 * total até aqui, não um delta: quem desenha mostra o último valor e pronto.
	 * Sem este quadro, uma escrita de minutos era silêncio absoluto na barra —
	 * indistinguível de travamento (o odontograma, 01/09). Opcional: um servidor
	 * mais antigo não o manda.
	 */
	/** `text`, quando vem, é o SUFIXO novo do JSON cru da chamada — concatenar. */
	onWriting?: (progress: { tool: string; chars: number; text?: string; lane?: string }) => void;
	/** Um escritor paralelo de uma missão (`/missions/:id/live`) mudou de estado. */
	onLane?: (lane: { lane: string; label: string; status: string; tool?: string }) => void;
	/** Uma descoberta estruturada da pesquisa de uma missão — o mesmo `id` atualiza a anterior. */
	onFound?: (found: Discovery) => void;
	/** Um evento e não um link no meio de um parágrafo, que seria mais um clique. */
	onNavigate?: (target: NavigateTarget) => void;
	/** Um arquivo que o turno entrega, no instante em que ele existe. Pode repetir
	 *  o que o quadro `done` também traz; quem acumula descarta pelo id. */
	onAttachment?: (file: TurnAttachment) => void;
	/**
	 * O BEN PREPAROU UMA RESPOSTA — e não a enviou.
	 *
	 * Um evento e não um texto no meio da fala, pelo mesmo motivo do `navigate`: o
	 * que muda o que a pessoa tem à frente vem num quadro próprio. Quem o recebe é
	 * a página do inbox, que o entrega ao compositor do operador — as palavras na
	 * caixa, o arquivo na fila. Nada sai daqui: quem envia é ele.
	 */
	onPreparedDraft?: (draft: PreparedDraft) => void;
	onDone?: (result: TurnDone) => void;
	onError?: (message: string) => void;
}

/** Para onde o SERVIDOR disse para ir. `to` chega pronto, nunca concatenado aqui
 *  nem pelo modelo: quem monta caminho de registro é o `SearchEverything` da fase
 *  3, e um segundo lugar que escreve `/contacts/${id}` erra quando a rota mudar. */
export interface NavigateTarget {
	to: string;
	label: string;
	/** Hoje o servidor manda só `to` e `label`: opcional em vez de inventado aqui. */
	kind?: string;
	reason?: string;
}

/**
 * O RASCUNHO que o Ben preparou, como ele chega.
 *
 * `fileIds` e nunca URLs: a imagem que o agente acabou de gerar tem endereço
 * ASSINADO, que expira em minutos, e um rascunho parado no compositor enquanto o
 * operador atende outra conversa quebraria no envio. O id é o que a fila de
 * anexos sabe resolver na hora certa (`addStored`).
 */
export interface PreparedDraft {
	text: string;
	fileIds: readonly string[];
	mediaKind?: "image" | "audio" | "video" | "document";
	caption?: string;
	/**
	 * A LINHA SEGURADA na conversa (07/09): o rascunho já é uma mensagem
	 * aguardando aprovação no fio. Presente, a tela recarrega o fio; ausente, o
	 * caminho antigo — o compositor recebe as palavras.
	 */
	heldMessageId?: string;
}

export interface TurnDone {
	reply: string;
	bubbles: string[];
	/** O raciocínio do provedor; e, à parte, o que ele escreveu fora da porta. */
	thinking: string[];
	withheld: string[];
	steps: Array<{ tool: string; input: string; output: string; ms: number; isError?: boolean }>;
	silent: boolean;
	/** O provedor recusou a pergunta: ela sai do histórico que se reenvia. */
	refused?: boolean;
	/**
	 * A chave que a pessoa DISSE e o Ben guardou no cofre: troca-se `from` por `to`
	 * nas bolhas, para o valor não voltar ao provedor no histórico que se reenvia.
	 */
	scrub?: Array<{ from: string; to: string }>;
	/**
	 * Os tokens do turno, SEPARADOS.
	 *
	 * `cachedInputTokens` e `reasoningTokens` chegavam no fio desde que o adaptador
	 * os passou a contar e eram deitados fora aqui — o painel somava entrada e
	 * saída num número só, e um turno caro não sabia dizer por quê. Opcionais
	 * porque um provedor que não os conte manda a resposta na mesma.
	 */
	usage: {
		inputTokens: number;
		outputTokens: number;
		cachedInputTokens?: number;
		reasoningTokens?: number;
	};
	/**
	 * A VIRADA PAROU À ESPERA DE UMA PESSOA — não acabou.
	 *
	 * Existia no servidor e não vinha no quadro. Opcional: um servidor anterior a
	 * 24/08 não o manda, e "não sei" tem de se ler como "não está à espera".
	 */
	awaitsHuman?: boolean;
	/**
	 * QUEM RESPONDEU, QUANTO CUSTOU, EM QUANTO TEMPO — e quantos créditos.
	 *
	 * Os mesmos quatro números que o servidor acabou de gravar na linha de
	 * auditoria. Sem eles o painel do Ben não tinha modelo, custo nem duração: a
	 * inbox desenhava-os lendo uma segunda rota, e a conversa — que é onde se está
	 * a olhar quando "porque é que isto foi caro?" ocorre — não tinha por onde.
	 * `null` quando o turno foi recusado pelo medidor: não correu nada.
	 */
	meter?: {
		execution?: ModelExecution;
		provider?: string;
		model?: string;
		costMicroUsd?: number;
		durationMs?: number;
		credits?: number;
	} | null;
	/**
	 * QUAL turno foi este.
	 *
	 * Sem ele o painel não tinha em que pendurar o polegar: a avaliação aponta para
	 * o turno, e a tela que acabou de assistir a um não sabia nomeá-lo. Null — e
	 * nunca string vazia — quando a linha de auditoria não pôde ser escrita, o que
	 * o servidor permite de propósito para não derrubar a resposta junto.
	 */
	turnId: string | null;
	/** O que perguntar agora. Opcional: um servidor que ainda não o manda funciona. */
	next?: string[];
	/**
	 * Os arquivos que este turno entrega.
	 *
	 * Cru de propósito: quem lê o fio não decide a forma de um anexo — quem decide é
	 * `toAttachment`, que é um leitor só para o evento `attachment` e para esta
	 * lista. Um servidor que só use uma das duas estradas funciona inteiro.
	 */
	attachments?: unknown[];
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * How the stream ended, as seen from this side of the wire.
 *
 * `dropped` is the one that used to lie on screens: the connection died, but the
 * SERVER may have finished the turn and archived the reply — so no handler is
 * called for it here, and the caller rereads the session before declaring
 * anything (see `recover-turn.ts`). Painting Safari's raw "Load failed" over a
 * finished turn is the exact bug this type exists to end.
 */
export type StreamOutcome = "done" | "error" | "aborted" | "dropped" | "none";

/**
 * Lê um corpo SSE até ao fim e diz COMO ele acabou.
 *
 * Partilhado por quem PEDE um turno e por quem VOLTA a um já a correr
 * (`live-turn.ts`): são o mesmo fio, com os mesmos quadros, e dois leitores
 * seriam duas oportunidades de divergirem no que entendem por "acabou".
 */
export async function followStream(
	body: ReadableStream<Uint8Array>,
	handlers: StreamHandlers,
	say: Say,
	signal: AbortSignal,
): Promise<StreamOutcome> {
	// Whether the server delivered its FINAL frame. Without one, a closed stream
	// is a cut stream, no matter how politely the proxy closed it.
	let ended: StreamOutcome | null = null;
	try {
		await pump(body, handlers, say, signal, (event) => {
			if (event === "done") ended = "done";
			if (event === "error") ended = "error";
			// Só a porta de reentrada o manda: não havia turno nenhum em voo.
			if (event === "none") ended = "none";
		});
	} catch {
		// An abort is the person interrupting, not a failure to report. Anything
		// else is a dead wire — and a dead wire is not a dead turn, so no raw
		// fetch message ever reaches the screen from here.
		if (signal.aborted) return "aborted";
	}
	return ended ?? "dropped";
}

export async function streamTurn(
	input: StreamInput,
	handlers: StreamHandlers,
	signal: AbortSignal,
	/** The caller's `t`. Two of the failures below are read by a person, and this
	 *  module has no scope of its own — see `tool-labels.ts` for the arrangement. */
	say: Say,
): Promise<StreamOutcome> {
	const token = await getAuthToken();
	// A barra do topo acende só enquanto o turno ESTÁ A SER ABERTO — do POST à
	// chegada dos cabeçalhos — e apaga quando o fio responde. Um turno do Ben dura
	// minutos; a barra acesa esse tempo todo seria uma barra presa, e o progresso
	// dali em diante já tem quem o mostre (a doca, ferramenta a ferramenta).
	const endConnecting = beginLoading("stream");
	const headersController = new AbortController();
	const headersTimer = setTimeout(() => headersController.abort(), 45_000);
	let response: Response;
	try {
		response = await fetch(`${API_BASE}/v3/agents/run/stream`, {
			method: "POST",
			headers: {
				"content-type": "application/json",
				...(token ? { authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify(bodyOf(input)),
			signal: AbortSignal.any([signal, headersController.signal]),
		});
	} catch {
		// The POST itself failed to produce a response. The request may still have
		// reached the server, so this is not death either — reconcile, then judge.
		endConnecting(!signal.aborted);
		return signal.aborted ? "aborted" : "dropped";
	} finally {
		clearTimeout(headersTimer);
	}
	endConnecting(!response.ok || !response.body);

	if (!response.ok || !response.body) {
		handlers.onError?.(
			response.status === 401
				? say("error.expired")
				: say("error.couldNotStart", { status: response.status }),
		);
		return "error";
	}

	return followStream(response.body, handlers, say, signal);
}

/** Reads the whole body, delivering every complete frame to `dispatch`. */
async function pump(
	body: ReadableStream<Uint8Array>,
	handlers: StreamHandlers,
	say: Say,
	signal: AbortSignal,
	track: (event: string | null) => void,
): Promise<void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let idle = false;
	const cancel = () => {
		void reader.cancel().catch(() => undefined);
	};
	let timer: ReturnType<typeof setTimeout> | undefined;
	const heartbeat = () => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			idle = true;
			cancel();
		}, 45_000);
	};
	signal.addEventListener("abort", cancel);
	if (signal.aborted) cancel();
	heartbeat();

	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) {
				if (idle || signal.aborted) throw new Error("Stream interrupted");
				break;
			}
			heartbeat();
			// Sinal de vida, antes de saber se isto é um quadro: um batimento não é.
			handlers.onBeat?.();

			// A chunk is NOT a message: the same rule as on the server. Anything
			// that parses per chunk works locally and drops tokens over a real
			// connection.
			buffer += decoder.decode(value, { stream: true });
			buffer = buffer.replace(/\r\n/g, "\n");

			let boundary = buffer.indexOf("\n\n");
			while (boundary >= 0) {
				const event = dispatch(buffer.slice(0, boundary), handlers, say);
				track(event);
				if (event === "done" || event === "error" || event === "none") {
					cancel();
					return;
				}
				buffer = buffer.slice(boundary + 2);
				boundary = buffer.indexOf("\n\n");
			}
		}
		if (buffer.trim()) track(dispatch(buffer, handlers, say));
	} finally {
		clearTimeout(timer);
		signal.removeEventListener("abort", cancel);
		reader.releaseLock();
	}
}

/**
 * O rascunho, se houver rascunho.
 *
 * Sem palavras E sem arquivos não é rascunho nenhum: a faixa "preparada pelo
 * Ben" sobre uma caixa vazia seria a tela a mentir, que é exatamente o
 * defeito que este lote existe para acabar.
 */
function toPreparedDraft(payload: Record<string, unknown>): PreparedDraft | null {
	const text = typeof payload.text === "string" ? payload.text : "";
	const fileIds = Array.isArray(payload.fileIds)
		? payload.fileIds.filter((id): id is string => typeof id === "string" && id.length > 0)
		: [];
	if (!text && fileIds.length === 0) return null;
	return {
		text,
		fileIds,
		...(typeof payload.mediaKind === "string"
			? { mediaKind: payload.mediaKind as PreparedDraft["mediaKind"] }
			: {}),
		...(typeof payload.caption === "string" && payload.caption ? { caption: payload.caption } : {}),
		...(typeof payload.heldMessageId === "string" && payload.heldMessageId
			? { heldMessageId: payload.heldMessageId }
			: {}),
	};
}

/** Sem `to` não há para onde ir, e um cartão vazio na conversa é pior que nenhum. */
function toNavigate(payload: Record<string, unknown>): NavigateTarget | null {
	if (typeof payload.to !== "string" || !payload.to) return null;
	return {
		to: payload.to,
		label: String(payload.label ?? payload.to),
		...(typeof payload.kind === "string" ? { kind: payload.kind } : {}),
		...(typeof payload.reason === "string" ? { reason: payload.reason } : {}),
	};
}

function toWriting(payload: Record<string, unknown>) {
	return {
		tool: String(payload.tool ?? ""),
		chars: Number(payload.chars ?? 0),
		// Ausente quando a ferramenta é redigida (segredo): só o tamanho vem.
		...(typeof payload.text === "string" ? { text: payload.text } : {}),
		// Só no fio de uma missão: `main` (o Ben) ou o id da tarefa de um escritor.
		...(typeof payload.lane === "string" ? { lane: payload.lane } : {}),
	};
}

function toLane(payload: Record<string, unknown>) {
	return {
		lane: String(payload.lane ?? ""),
		label: String(payload.label ?? payload.lane ?? ""),
		status: String(payload.status ?? ""),
		// A ferramenta dona da pista (04/10): `change_system` é uma parte do sistema; `web`, uma leitura.
		...(typeof payload.tool === "string" && payload.tool ? { tool: payload.tool } : {}),
	};
}

/** Um bloco SSE em nome e carga. `null` quando não há o que entregar. */
function parse(block: string): { event: string; payload: Record<string, unknown> } | null {
	let event = "message";
	const data: string[] = [];

	for (const line of block.split("\n")) {
		if (line === "" || line.startsWith(":")) continue;
		if (line.startsWith("event:")) event = line.slice(6).trim();
		else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
	}
	if (data.length === 0) return null;

	try {
		return { event, payload: JSON.parse(data.join("\n")) as Record<string, unknown> };
	} catch {
		// One malformed frame must not end the turn.
		return null;
	}
}

/** O texto e, quando vem, a raia de um quadro de pensamento. */
function laneText(payload: Record<string, unknown>): [text: string, lane?: string] {
	const text = String(payload.text ?? "");
	return typeof payload.lane === "string" ? [text, payload.lane] : [text];
}

/** Delivers one frame and names it, so the caller knows whether a FINAL frame
 *  (`done`/`error`) ever arrived — the difference between ended and cut. */
function dispatch(block: string, handlers: StreamHandlers, say: Say): string | null {
	const frame = parse(block);
	if (!frame) return null;
	const payload = frame.payload;

	switch (frame.event) {
		case "queued":
		case "queued_applied":
			handlers.onQueued?.(
				{
					id: String(payload.id ?? ""),
					text: String(payload.text ?? ""),
					...(payload.origin === "agent" ? { origin: "agent" as const } : {}),
				},
				frame.event === "queued_applied",
			);
			break;
		case "session": {
			const id = String(payload.id ?? "");
			if (id) handlers.onSession?.(id);
			break;
		}
		case "prompt":
			handlers.onPrompt?.(String(payload.text ?? ""));
			break;
		case "delta":
			handlers.onDelta?.(String(payload.text ?? ""));
			break;
		case "thinking":
			handlers.onThinking?.(...laneText(payload));
			break;
		case "thought":
			handlers.onThought?.(...laneText(payload));
			break;
		case "translation":
			handlers.onTranslation?.(String(payload.to ?? ""));
			break;
		case "tool_call":
			handlers.onToolCall?.(payload as never);
			break;
		case "tool_result":
			handlers.onToolResult?.(payload as never);
			break;
		case "step":
			handlers.onStep?.(Number(payload.index ?? 0));
			break;
		case "tool_tasks":
			if (Array.isArray(payload.tasks))
				handlers.onToolTasks?.({
					id: String(payload.id ?? ""),
					tasks: payload.tasks as ToolTask[],
				});
			break;
		case "writing":
			handlers.onWriting?.(toWriting(payload));
			break;
		case "lane":
			handlers.onLane?.(toLane(payload));
			break;
		case "found": {
			const found = toDiscovery(payload);
			if (found) handlers.onFound?.(found);
			break;
		}
		case "navigate": {
			const target = toNavigate(payload);
			if (target) handlers.onNavigate?.(target);
			break;
		}
		case "draft": {
			const draft = toPreparedDraft(payload);
			if (draft) handlers.onPreparedDraft?.(draft);
			break;
		}
		case "attachment": {
			const file = toAttachment(payload);
			if (file) handlers.onAttachment?.(file);
			break;
		}
		case "done":
			handlers.onDone?.(payload as unknown as TurnDone);
			break;
		case "error":
			// The server's own sentence when it sent one — it is the only thing that
			// says what actually happened.
			handlers.onError?.(String(payload.message ?? say("error.somethingWentWrong")));
			break;
	}
	return frame.event;
}
